/**
 * EmptyView Component
 *
 * A consistent empty state component used across the application.
 * Supports optional icon, title, description, and action buttons for flexible empty states.
 */

import React from 'react';
import { LucideIcon } from 'lucide-react';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@loader/ui';
import { Card } from '@loader/ui';

import { cn } from '@/lib/utils';

export interface EmptyViewProps {
  /**
   * Title text displayed in the empty state
   */
  title: string;

  /**
   * Description text displayed below the title
   */
  description?: string;

  /**
   * Optional icon component to display
   */
  icon?: LucideIcon;

  /**
   * Whether to wrap the empty state in a Card component
   * @default false
   */
  withCard?: boolean;

  /**
   * Additional CSS classes for the container
   */
  className?: string;

  /**
   * Additional CSS classes for the Empty component
   */
  emptyClassName?: string;

  /**
   * Optional action buttons or content to display below the description
   * This is typically used for call-to-action buttons
   */
  children?: React.ReactNode;
}

/**
 * EmptyView - A consistent empty state component
 * 
 * @example
 * ```tsx
 * <EmptyView
 *   title="No data available"
 *   description="This table doesn't contain any records."
 *   icon={Database}
 * />
 * ```
 *
 * @example
 * ```tsx
 * <EmptyView
 *   title="No linked tables found"
 *   description="Create your first linked table to get started."
 *   withCard={true}
 * />
 * ```
 */
export const EmptyView: React.FC<EmptyViewProps> = ({
  title,
  description,
  icon: Icon,
  withCard = false,
  className,
  emptyClassName,
  children,
}) => {
  const emptyContent = (
    <Empty className={cn('h-full', emptyClassName)}>
      <EmptyHeader>
        {Icon && (
          <EmptyMedia variant="icon">
            <Icon className="h-8 w-8" />
          </EmptyMedia>
        )}
        <EmptyTitle>{title}</EmptyTitle>
        {description && <EmptyDescription>{description}</EmptyDescription>}
      </EmptyHeader>
      <EmptyContent>{children}</EmptyContent>
    </Empty>
  );

  if (withCard) {
    return (
      <div className={cn('h-full w-full', className)}>
        <Card className="h-full w-full">{emptyContent}</Card>
      </div>
    );
  }

  return <div className={className}>{emptyContent}</div>;
};


